'use client';

import { useParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { ArrowLeft, Phone, MapPin, Search, Tag, ExternalLink, Globe } from 'lucide-react';
import Footer from '@/components/Footer';
import { COMERCIO_DATA } from '@/lib/comercio-data';
import { useState } from 'react';


export default function CategoryPage() {
  const params = useParams();
  const slug = params.category as string;
  const [searchTerm, setSearchTerm] = useState('');

  const categoryData = COMERCIO_DATA.find(cat => cat.id === slug);

  if (!categoryData) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4">
        <h1 className="text-2xl font-bold text-slate-800 mb-4">Categoria não encontrada</h1>
        <Link to="/comercio" className="text-emerald-600 font-bold hover:underline">Voltar para o Guia Comercial</Link>
      </div>
    );
  }

  const filteredItems = categoryData.items.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-emerald-200 selection:text-emerald-900">
      {/* Header */}
      <header className={`${categoryData.color} text-white shadow-lg relative overflow-hidden`}>
        <div className="absolute inset-0 bg-black/20" />
        <div className="relative max-w-4xl mx-auto px-4 py-12 md:py-20 text-center space-y-6">
          <Link 
            to="/comercio" 
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 px-6 py-3 rounded-2xl text-white font-black uppercase tracking-tighter italic transition-all hover:-translate-x-1 active:scale-95 mb-8 group shadow-lg"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Voltar para Categorias
          </Link>
          <div className="flex flex-col items-center gap-4">
            <div className="p-5 rounded-3xl bg-white/20 backdrop-blur-md shadow-xl">
              {categoryData.icon}
            </div>
            <h1 className="text-3xl md:text-5xl font-black tracking-tighter uppercase italic">
              {categoryData.category}
            </h1>
          </div>
          <p className="text-white/80 text-lg font-medium max-w-2xl mx-auto">
            Telefones e endereços das empresas de <span className="text-white font-bold underline decoration-white/30">{categoryData.category}</span> em Catanduvas PR.
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 -mt-8 pb-20">
        {/* Search Bar */}
        <div className="relative group mb-10">
          <div className="absolute inset-y-0 left-0 pl-6 flex items-center pointer-events-none">
            <Search className="h-6 w-6 text-slate-400 group-focus-within:text-emerald-600 transition-colors" />
          </div>
          <input
            type="text"
            placeholder={`Buscar em ${categoryData.category}...`}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="block w-full pl-16 pr-6 py-6 bg-white border-none rounded-[2rem] text-xl shadow-2xl focus:ring-4 focus:ring-emerald-500/20 transition-all outline-none placeholder:text-slate-400"
          />
        </div>

        {/* Business List */}
        <div className="grid grid-cols-1 gap-6">
          {filteredItems.map((item, index) => (
            <div 
              key={index} 
              className="bg-white rounded-[2.5rem] p-8 shadow-sm border border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-8 group hover:shadow-xl transition-all"
            >
              <div className="flex-1">
                <h3 className="text-2xl font-black text-slate-800 uppercase tracking-tighter italic mb-4 group-hover:text-emerald-600 transition-colors">
                  {item.name}
                </h3>
                {item.description && (
                  <p className="text-slate-600 font-medium mb-3">
                    {item.description}
                  </p>
                )}
                {item.address && (
                  <div className="flex items-start gap-3 text-slate-500 font-medium">
                    <MapPin className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                    <span>{item.address}</span>
                  </div>
                )}
              </div>

              <div className="flex flex-col gap-3 shrink-0 w-full md:w-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 md:flex md:flex-col gap-3">
                  <a 
                    href={`tel:${item.number.replace(/\D/g, '')}`}
                    className="flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 py-4 px-6 rounded-2xl font-black uppercase tracking-tighter italic transition-all active:scale-95"
                  >
                    <Phone className="w-5 h-5" />
                    {item.number}
                  </a>
                  {item.whatsapp && (
                    <a 
                      href={item.whatsapp}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 py-4 px-6 rounded-2xl font-black uppercase tracking-tighter italic transition-all active:scale-95"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                      WhatsApp
                    </a>
                  )}
                  {item.app && (
                    <a 
                      href={item.app}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 bg-blue-100 hover:bg-blue-200 text-blue-700 py-4 px-6 rounded-2xl font-black uppercase tracking-tighter italic transition-all active:scale-95"
                    >
                      <ExternalLink className="w-5 h-5" />
                      Pedir no App
                    </a>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredItems.length === 0 && (
          <div className="text-center py-20 bg-white rounded-[2.5rem] border border-dashed border-slate-300">
            <Tag className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-slate-800">Nenhuma empresa encontrada</h3>
            <p className="text-slate-500 mt-2">Ainda não temos empresas cadastradas nesta categoria ou o termo buscado não retornou resultados.</p>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}
